import React, { useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useIntl, useSelector } from 'umi';
import { useDebounceFn, useSetState, useSize } from 'ahooks';
import { hasReportDocument, removeTags } from '@/pages/aigc/pages/Agent/ChatPage/utils';
import { isEmpty, last } from 'lodash-es';
import { useErrorTips } from '@/pages/aigc/hooks/useErrorTips';
import classNames from 'classnames';
import Abnormal from '@home/components/Abnormal';
import DefauletFile from '@/pages/aigc/assets/svg/agent-ds-temp-file.svg';
import { Icon, List, Modal, notification } from 'tea-component';
import BasicImage from '@home/components/BasicImage';
import moment from 'moment';
import defaultFileIcon from '@/pages/aigc/assets/svg/agent-document-file.svg';
import CommonBubble from '@home/components/CommonBubble';
import SvgIcon from '@home/components/SvgIcon';
import { copyToClipboard } from '@/pages/aigc/utils/utils';
import { useDocumentOperations } from '../../ChatDSTemplate/hooks';
import { CustomDropdown } from '../../ChatDSTemplate/components';
import { MainContentCmp } from '../../ChatPage/components/SubComp';
import EllipsisHover from '@gp/components/EllipsisHover';
import { useChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import { useContentExporter } from '@/pages/aigc/hooks/useContentExporter';
import { CHAT_DS_TEMPLATE_ACTIONS, CHAT_DS_TEMPLATE_ACTIONS_LIST, CHAT_DS_TEMPLATE_FILE_Type, ChatDSFileTypeExport } from '../../ChatDSTemplate/constants';
import { ModalType, ReportActions } from '../../ChatDSTemplate/type';
import dataBrainIcon from '@/pages/aigc/pages/Agent/svg/databrian_logo_text.png';
import '../../ChatDSTemplate/index.scss';
import '../../ChatDSTemplate/components/ChatDSTemplateActionsList.scss';

type ChatDSTemplateActionsShareListProps = {
  handleUpdateDsReport: (data: any) => void;
  conversations: any[];
  dsReport: any;
  sessionId: string;
}

export const ChatDSTemplateActionsShareList = ({
  handleUpdateDsReport,
  conversations,
  dsReport,
  sessionId
  // streamingState
}: ChatDSTemplateActionsShareListProps) => {
  const userInfo = useSelector(
    (state: any) => state.home.userInfo,
    shallowEqual,
  );
  const { chatList } = useChatList();
  const { formatMessage, locale } = useIntl();
  const [active, setActive] = useSetState<{}>({
    // 步骤
    stepActive: 'framework',
    // 操作
    fileActive: '',
  });
  // 报告text
  const [reportValue, setReportValue] = useSetState({
    htmlReport: ``,
  });
  const [showConfig, setShowConfig] = useSetState<{ showModal: ModalType | undefined; showType: string; }>({
    showModal: undefined,
    showType: ''
  });
  const { showModal, showType } = showConfig || {};

  // 页签高亮（文件列表、顶部页签）
  const { stepActive, fileActive } = active || {};
  const {
    document_id,
    // version,
    type: initialType,
    answer,
    messageId,
  } = dsReport || {};
  const { htmlReport } = reportValue || {};
  const isEn = locale === 'en-US';
  const {
    fetchDocument,
    getFileList,
    loading,
    listLoading,
    reportFileList,
  } = useDocumentOperations({
    stepActive, callBack: () => {
      handleUpdateDsReport({ reportId: last(conversations)?.messageId });
    }
  });
  const { exportPdf, exportTxt, exportWord } = useContentExporter();
  const reportRef = useRef<HTMLDivElement>(null);
  const showErrorTips = useErrorTips();
  // 复制反馈tips
  const [showCopyTips, setShowCopyTips] = useState(false);
  const { width } = useSize(document.querySelector('.dba-chat-ds-report-page-right-container')) || { width: 0};

  // 初始化文档数据
  useEffect(() => {
    const initDocument = async () => {
      if (!document_id) return;
      const reportRes = await getFileList({ document_type: initialType || 'framework' });
      const { id, version } = reportRes?.documents?.[0] || { id: '', version: '' };
      const res = await fetchDocument(id, version, initialType);
      setReportValue({
        htmlReport: res?.text,
      });
      setActive({
        fileActive: id + version,
        stepActive: initialType || 'framework',
      });
    };
    initDocument();
  }, [initialType, messageId]);

  // 处理页面卸载
  useEffect(() => () => {
    handleUpdateDsReport({ show: false });
  }, []);


  // 处理文件项点击事件
  const handleFileItemClick = async (
    item: { id: any; title?: string; version?: any; },
    currentFileActive: string,
    closeModal: () => void,
  ) => {
    if (item?.id === currentFileActive) return;
    const res = await fetchDocument(item?.id, item?.version);
    setActive({ fileActive: item.id + item?.version });
    setReportValue({
      htmlReport: res?.text,
    });
    closeModal();
  };

  // 获取文件menu，及按钮文案
  const [menu, currentButton] = useMemo(() => {
    const currentButton = reportFileList?.documents?.find(
      (item) => item?.id + item?.version === fileActive || item?.id === document_id,
    );

    return [
      (close) => (
        <List type="option">
          {reportFileList?.documents?.map(
            (item: { id: React.Key | null | undefined; title: string; }) => (
              <List.Item
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={() => handleFileItemClick(item, fileActive, close)}
                key={item?.id}
                selected={fileActive === item?.id + item?.version}
              >
                <div className="dba-chat-ds-report-page-right-file-actions-box-item">
                  <div>
                    <BasicImage
                      src={defaultFileIcon}
                      errorSrc={defaultFileIcon}
                    />
                  </div>
                  <div style={{ maxWidth: `${width - 120}px`}}>
                    <EllipsisHover text={item?.title} />
                    <span>{moment(item?.date_time).utcOffset(+8).format('YYYY-MM-DD HH:mm')}</span>
                  </div>
                </div>
              </List.Item>
            ),
          )}
        </List>
      ),
      currentButton,
    ];
  }, [reportFileList, active, document_id, fileActive, width]);

  // 获取按钮列表
  const actionsList = useMemo(
    () =>
      CHAT_DS_TEMPLATE_ACTIONS_LIST(isEn, '', showCopyTips)?.filter(
        (item) => item.value === stepActive && [ReportActions.ReportCopy, ReportActions.ReportExport].includes(item.key),
      ),
    [isEn, stepActive, showCopyTips],
  );

  // 是否禁用分析报告
  const isDisabledReportTab =
    !hasReportDocument(conversations) && initialType !== 'report';

  const sensitiveConversation = useMemo(
    () =>
      chatList.find((chat) => chat.session_id === sessionId)
        ?.has_sensitive_data,
    [chatList, sessionId],
  );

  // actions 事件
  const { run: handleAction } = useDebounceFn(
    ({ key }: { key: string; }) => {
      const actionsHandlers: Partial<Record<ReportActions, () => void | Promise<void>>> = {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        [ReportActions.ReportCopy]: async () => {
          const coptStatus = await copyToClipboard(removeTags(htmlReport));
          if (showCopyTips) return;
          if (coptStatus) {
            setShowCopyTips(true);
            const timer = setTimeout(() => {
              setShowCopyTips(false);
              clearTimeout(timer);
            }, 2000);
          }
        },
        [ReportActions.ReportExport]: () => {
          // console.log('export');
        },
      } as const;
      const handler = actionsHandlers[key as ReportActions];
      // 执行对应的处理函数
      if (handler) {
        handler();
      }
    },
    {
      wait: 300,
      leading: true,
    },
  );

  // 点击步骤事件
  const handleStepClick = async (step: string) => {
    step !== stepActive && setShowCopyTips(false);
    setActive({ stepActive: step });
    try {
      const res = await getFileList({ document_type: step });
      const { id: document_id, version } = res?.documents?.[0];
      const reportRes = await fetchDocument(document_id, version, step);
      setReportValue({
        // initialValue: reportRes?.text,
        htmlReport: reportRes?.text,
      });
      setActive({
        fileActive: document_id + version,
      });
    } catch (error) {
      console.error('Error in handleStepClick:', error);
    }
  };

  // 导出事件（基于类型映射）
  const { run: handleExport } = useDebounceFn((type: string, isExport = false) => {
    const el = reportRef.current as HTMLElement | null;
    if (!el) return;
    const baseName = currentButton?.display_title || currentButton?.title;
    const watermark = `${userInfo?.username || 'User'}@DataBrain`;

    const map: Record<string, () => void | Promise<void>> = {
      [ChatDSFileTypeExport.PDF]: () => exportPdf(el, { filename: `${baseName}.pdf`, watermark }),
      [ChatDSFileTypeExport.TXT]: () => exportTxt(el, `${baseName}.txt`),
      [ChatDSFileTypeExport.Word]: () => exportWord(el, `${baseName}.doc`, { watermark }),
    };

    const runner = map[type];
    if (runner) {
      if (sensitiveConversation && !isExport) {
        setShowConfig({
          showModal: ModalType.Export,
          showType: type
        });
        return;
      }
      handleContentConfirmation(runner, [ChatDSFileTypeExport.PDF].includes(type as ChatDSFileTypeExport));
    };
  }, {
    wait: 300,
    leading: true
  });

  const handleContentConfirmation = async (
    mutate: () => void | Promise<void>,
    showProgress: boolean,
  ): Promise<void> => {
    if (!showProgress) {
      try {
        await Promise.resolve(mutate());
        notification.success({
          description: formatMessage({ id: 'dba_download_success' }),
          duration: 2000,
        });
      } catch (error) {
        showErrorTips(1005, error);
      } finally {
        setShowConfig({ showModal: undefined });
      }
      return;
    }

    const { destroy } = notification.warning({
      description: (
        <>
          <Icon type="loading" />
          {formatMessage({ id: 'dba_downloading' })}
        </>
      ),
      duration: 2000,
      className: 'dba-pdf-export-notification-loading',
    });

    try {
      await Promise.resolve(mutate());
      destroy();
      notification.success({
        description: formatMessage({ id: 'dba_download_success' }),
        duration: 2000,
      });
    } catch (error) {
      destroy();
      showErrorTips(1005, error);
    } finally {
      setShowConfig({ showModal: undefined });
    }
  };

  const modalContent = useMemo(() => {
    switch (showModal) {
      case ModalType.Export:
        return {
          caption: formatMessage({ id: "dba_ds_report_export" }),
          bodyDesc: formatMessage({ id: "dba_ds_report_export_content" }),
          showCancel: false,
          onClick: () => handleExport(showType, true),
        };
      default:
        return undefined;
    }
  }, [showModal, showType]);

  return (
    <div className="dba-chat-ds-report-page-right">
      <div className="dba-chat-ds-report-page-right-container">
        <div className="dba-chat-ds-report-page-right-file">
          <div className="dba-chat-ds-report-page-right-file-list">
            {CHAT_DS_TEMPLATE_ACTIONS?.map((task, index) => (
              <CommonBubble
                placement="right"
                key={index}
                dark
                closeOnScroll
                destroyOnClose
                content={
                  isDisabledReportTab &&
                  index === 1 && (
                    <div>
                      {formatMessage({ id: 'dba_report_step_disabled_tip' })}
                    </div>
                  )
                }
              >
                <div
                  className={classNames(
                    'dba-chat-ds-report-page-right-file-item',
                    {
                      'dba-chat-ds-report-page-right-file-active':
                        stepActive === task.value,
                      'dba-chat-ds-report-page-right-file-disabled':
                        index === 1 && isDisabledReportTab,
                    },
                  )}
                  onClick={() => {
                    !isDisabledReportTab && handleStepClick(task.value);
                  }}
                >
                  {`${index + 1}. ${isEn ? task.labelEn : task.label}`}
                </div>
              </CommonBubble>
            ))}
          </div>
          <SvgIcon
            type="clonse"
            onClick={() => handleUpdateDsReport({ show: false })}
          />
        </div>
        <div className="dba-chat-ds-report-page-right-file-actions">
          <div>
            {!isEmpty(reportFileList?.documents) && (
              <CustomDropdown
                {...{
                  menu,
                  button: (
                    <>
                      <img src={DefauletFile} alt="" />
                      <div style={{ maxWidth: `${width - (stepActive !== 'report' ? 100 : 320)}px`}}>
                        {<EllipsisHover text={currentButton?.title} />}
                      </div>
                    </>
                  ),
                  boxClassName:
                    'dba-chat-ds-report-page-right-file-actions-box',
                }}
              />
            )}
          </div>
          <div className="dba-chat-ds-report-page-right-file-actions-list">
            {actionsList.map((item, index) => (
              <div
                className="dba-chat-ds-report-page-right-file-actions-list-item"
                key={index}
                onClick={() => {
                  handleAction(item);
                }}
              >
                {item.key !== ReportActions.ReportExport && item.label}
                {item.key === ReportActions.ReportExport && (
                  <CustomDropdown
                    {...{
                      appearance: 'pure',
                      button: <>{item.label}</>,
                      menu: () => (
                        <List type="option" style={{ maxWidth: '300px' }}>
                          {CHAT_DS_TEMPLATE_FILE_Type?.map((item) => (
                            <List.Item key={item.value} onClick={() => handleExport(item.value)}>{item.label}</List.Item>
                          ))}
                        </List>
                      ),
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="dba-chat-ds-report-page-right-markdown-box">
        <div ref={reportRef} className="dba-chat-ds-report-page-right-markdown">
          <div className='dba-ds-report-top-logo'>
            <img src={dataBrainIcon} className='dba-ds-report-top-logo-word-img' crossOrigin='anonymous' />
          </div>
          {/* 底部 */}
          <Abnormal
            component={
              <MainContentCmp
                {...{
                  value: htmlReport,
                  answer,
                }}
              />
            }
            isLoading={listLoading || loading}
            isError={false}
            isNoData={isEmpty(htmlReport)}
          />
        </div>
      </div>
      {showModal && <Modal
        className='dba-pdf-export-modal'
        caption={modalContent?.caption}
        visible={true}
        onClose={() => setShowConfig({ showModal: undefined })}
      >
        <Modal.Body>{modalContent?.bodyDesc}</Modal.Body>
        <Modal.Footer>
          <button
            className='dba-pdf-export-modal-btn-confirm'
            onClick={modalContent?.onClick}
          > {formatMessage({ id: 'dba_confirm' })} </button>

          {showModal && modalContent?.showCancel && <button
            className='dba-pdf-export-modal-btn'
            onClick={() => setShowConfig({ showModal: undefined })}
          > {formatMessage({ id: 'dba_input_cancel' })} </button>}
        </Modal.Footer>
      </Modal>}
    </div>
  );
};